import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import AppBreadCrumb from '../../../components/common/AppBreadCrumb';
import AppToaster from '../../../components/AppToaster';
import { getReportParameters, generateReport } from '../../../api/ReportsAPI';
import { getUsers } from '../../../api/UsersAPI'; // Import your existing UsersAPI
import { capitalizeFirstLetter } from '../../../commonfunctions';
import { format } from 'date-fns';

const GenericReportsPage = ({ moduleTitle, module }) => {
    // --- State ---
    const [config, setConfig] = useState(null);
    const [loadingConfig, setLoadingConfig] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [pdfUrl, setPdfUrl] = useState(null);
    const [toaster, setToaster] = useState({});
    const [showToaster, setShowToaster] = useState(false);

    // --- Selections ---
    const [selectedReport, setSelectedReport] = useState(null);
    const [reportType, setReportType] = useState('abstract');
    const [reportFormat, setReportFormat] = useState('pdf');

    // --- Dynamic Filters State (Adapted for Club Reports) ---
    const [filters, setFilters] = useState({
        from_date: new Date(),
        to_date: new Date(),
        user_id: null, // Replaced party_id with user_id
        status_filter: 'all' // New filter for balance sheet
    });

    // --- Master Data for Dropdowns ---
    const [userOptions, setUserOptions] = useState([]); // Replaced partyOptions

    // --- 1. Load Report Configuration & Masters ---
    useEffect(() => {
        let isMounted = true;
        setLoadingConfig(true);
        
        // 1. Fetch Report Config
        getReportParameters(module, (res) => {
            if (!isMounted) return;
            if (res.currentPageControl) {
                setConfig(res.currentPageControl);
                const firstReport = Object.keys(res.currentPageControl)[0];
                if (firstReport) setSelectedReport(firstReport);
            }
            setLoadingConfig(false);
        });

        // 2. Pre-fetch Users (Members) for the filter
        getUsers({ paginate: false }, (res) => {
            if (!isMounted) return;
            const fetchedMembers = res.users?.data || res.users || [];
            setUserOptions(fetchedMembers.map(u => ({ value: u.id, label: u.name })));
        });

        return () => { isMounted = false; };
    }, [module]);

    // --- Helpers ---
    const getActiveFilters = () => {
        if (!config || !selectedReport) return [];
        return config[selectedReport]?.[reportType]?.filters || [];
    };
    const hasFilter = (filterName) => getActiveFilters().includes(filterName);

    // --- Handlers ---
    const handleGenerate = () => {
        setGenerating(true);
        if (pdfUrl) URL.revokeObjectURL(pdfUrl);
        setPdfUrl(null);

        const payload = {
            selectedOption: selectedReport,
            selectedType: reportType,
            reportFormat: reportFormat,
            from_date: format(filters.from_date, 'yyyy-MM-dd'),
            to_date: format(filters.to_date, 'yyyy-MM-dd'),
            user_id: filters.user_id, // Use user_id
            filter: filters.status_filter, // Use 'filter' for balance status
        };

        generateReport(module, payload, (response) => {
            setGenerating(false);
            if (response.error) {
                setToaster({ type: 'error', message: 'Report generation failed.' });
                setShowToaster(true);
                return;
            }

            if (reportFormat === 'pdf') {
                const file = new Blob([response], { type: 'application/pdf' });
                const fileURL = URL.createObjectURL(file);
                setPdfUrl(fileURL);
            } else if (reportFormat === 'spreadsheet' && response.success) {
                const link = document.createElement('a');
                link.href = 'data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,' + response.file;
                link.download = response.filename || 'report.xlsx';
                link.click();
                setToaster({ type: 'success', message: 'Report downloaded successfully.' });
                setShowToaster(true);
            }
        });
    };

    return (
        <>
            <AppToaster showToaster={showToaster} setShowToaster={setShowToaster} toaster={toaster} />
            <main className="container-fluid">
                <AppBreadCrumb title={`${moduleTitle} Reports`} />
                
                <div className="row">
                    {/* --- Left Sidebar: Report Controls --- */}
                    <div className="col-md-4 col-lg-3">
                        <div className="card">
                            <div className="card-header"><h5>Report Options</h5></div>
                            <div className="card-body">
                                {loadingConfig ? (
                                    <div className="text-center"><div className="spinner-border"></div></div>
                                ) : (
                                    <form onSubmit={e => e.preventDefault()}>
                                        {/* 1. Select Report */}
                                        <div className="mb-3">
                                            <label className="form-label">Select Report</label>
                                            <select className="form-select" value={selectedReport || ''} onChange={e => setSelectedReport(e.target.value)}>
                                                {config && Object.keys(config).map(key => (
                                                    <option key={key} value={key}>
                                                        {capitalizeFirstLetter(key.replace(/-/g, ' '))}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        {/* 2. Report Type (Abstract/Detail) */}
                                        <div className="mb-3">
                                            <label className="form-label d-block">Report Type</label>
                                            <div className="btn-group w-100" role="group">
                                                <input type="radio" className="btn-check" name="reportType" id="rt_abstract" value="abstract" checked={reportType === 'abstract'} onChange={() => setReportType('abstract')} disabled={!config?.[selectedReport]?.abstract} />
                                                <label className="btn btn-outline-primary" htmlFor="rt_abstract">Abstract</label>

                                                <input type="radio" className="btn-check" name="reportType" id="rt_detail" value="detail" checked={reportType === 'detail'} onChange={() => setReportType('detail')} disabled={!config?.[selectedReport]?.detail} />
                                                <label className="btn btn-outline-primary" htmlFor="rt_detail">Detailed</label>
                                            </div>
                                        </div>
                                        <hr />
                                        <h6 className="mb-2">Filters</h6>

                                        {/* 3. Dynamic Filters */}
                                        {hasFilter('date-range') && (
                                            <div className="mb-3">
                                                <label className="form-label">Date Range</label>
                                                <div className="input-group mb-2">
                                                    <span className="input-group-text">From</span>
                                                    <DatePicker selected={filters.from_date} onChange={date => setFilters({...filters, from_date: date})} className="form-control" dateFormat="yyyy-MM-dd" />
                                                </div>
                                                <div className="input-group">
                                                    <span className="input-group-text" style={{minWidth: '58px'}}>To</span>
                                                    <DatePicker selected={filters.to_date} onChange={date => setFilters({...filters, to_date: date})} className="form-control" dateFormat="yyyy-MM-dd" />
                                                </div>
                                            </div>
                                        )}

                                        {hasFilter('user') && (
                                            <div className="mb-3">
                                                <label className="form-label">Member (Optional)</label>
                                                <Select 
                                                    options={userOptions} 
                                                    isClearable
                                                    placeholder="All Members"
                                                    onChange={opt => setFilters({...filters, user_id: opt?.value || null})}
                                                />
                                            </div>
                                        )}

                                        {hasFilter('status_filter') && (
                                            <div className="mb-3">
                                                <label className="form-label">Balance Status</label>
                                                <select className="form-select" value={filters.status_filter} onChange={e => setFilters({...filters, status_filter: e.target.value})}>
                                                    <option value="all">All Members</option>
                                                    <option value="negative_balance">Negative Balance Only</option>
                                                </select>
                                            </div>
                                        )}
                                        
                                        <hr />
                                        
                                        {/* 4. Output Format */}
                                        <div className="mb-3">
                                            <label className="form-label">Output Format</label>
                                            <div className="d-flex gap-3">
                                                <div className="form-check">
                                                    <input className="form-check-input" type="radio" name="rFmt" id="rf_pdf" checked={reportFormat === 'pdf'} onChange={() => setReportFormat('pdf')} />
                                                    <label className="form-check-label" htmlFor="rf_pdf"><i className="fas fa-file-pdf text-danger"></i> PDF</label>
                                                </div>
                                                <div className="form-check">
                                                    <input className="form-check-input" type="radio" name="rFmt" id="rf_xls" checked={reportFormat === 'spreadsheet'} onChange={() => setReportFormat('spreadsheet')} />
                                                    <label className="form-check-label" htmlFor="rf_xls"><i className="fas fa-file-excel text-success"></i> Excel</label>
                                                </div>
                                            </div>
                                        </div>

                                        <button className="btn btn-primary w-100" onClick={handleGenerate} disabled={generating || loadingConfig}>
                                            {generating ? <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>Generating...</> : 'Generate Report'}
                                        </button>
                                    </form>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* --- Right Side: Report Preview (PDF only) --- */}
                    <div className="col-md-8 col-lg-9">
                        <div className="card h-100">
                            <div className="card-header"><h5>Report Preview</h5></div>
                            <div classNamea="card-body p-0 d-flex align-items-center justify-content-center" style={{minHeight: '500px', background: '#f8f9fa'}}>
                                {generating && <div className="text-center p-5"><div className="spinner-border text-primary" style={{width: '3rem', height: '3rem'}}></div><h6 className="text-muted mt-3">Generating Report... Please wait.</h6></div>}
                                
                                {!generating && pdfUrl && reportFormat === 'pdf' && (
                                    <iframe src={pdfUrl} title="Report Preview" width="100%" height="800px" style={{border: 'none'}}></iframe>
                                )}

                                {!generating && !pdfUrl && (
                                    <div className="text-center text-muted p-5">
                                        <i className="fas fa-chart-bar fs-1 mb-3 d-block opacity-50"></i>
                                        <p>Select options and click "Generate Report" to view results here.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </>
    );
};

export default GenericReportsPage;